import { Component, Input, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { HttpClient } from '@angular/common/http';

import { UserService, UserDetails } from '../../services/user.service';
import { environment } from '../../../environments/environment';
import provinceData from '../../../environments/province_with_district_and_sub_district.json';
import { formatMemberExpiry, isLifetimeMemberType } from '../../utils/membership.util';

// Interface for CPD Hours API Response
interface CpdHoursResponse {
  id: string;
  hoursStudied: string;
  courseFor: string;
  studyDate: string;
  year: number;
  sessionTitle: string;
  notes: string;
  isVerified: boolean;
  enrolledAt: string;
  completedAt: string | null;
  progress: string;
  isCompleted: boolean;
  createdAt: string;
  updatedAt: string;
  userId: string;
  courseId: string;
  examId: string | null;
  cpdCourseStatusId: number;
  cpdPaymentStatusId: number;
  paymentSlip: string | null;
  amount: string | null;
  originalSlipStatus: boolean;
  image: string | null;
  imageThumbnail: string | null;
  certificationBySEC: boolean;
  user: {
    id: string;
    email: string;
    password: string;
    cid: string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
    companyId: string | null;
  };
  course: {
    id: string;
    title: string;
    courseFor: string;
    description: string;
    instructor: string;
    duration: string;
    category: string;
    level: string;
    thumbnail: string | null;
    price: string;
    seat: number;
    regisSeat: number;
    isActive: boolean;
    startDate: string | null;
    endDate: string | null;
    closeRegisterDate: string | null;
    certificationBySEC: boolean;
    trainingLocation: string;
    imageThumbnail: string | null;
    imageDetail: string | null;
    createdAt: string;
    updatedAt: string;
    courseStatusId: number;
  };
  cpdCourseStatus: {
    id: number;
    name: string;
  };
  cpdPaymentStatus: {
    id: number;
    name: string;
    createdAt: string;
    updatedAt: string;
  };
}

@Component({
  selector: 'app-user-detail-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './user-detail-modal.component.html',
  styleUrl: './user-detail-modal.component.scss'
})
export class UserDetailModalComponent implements OnInit, OnDestroy {
  private _userId!: string;

  @Input()
  set userId(value: string) {
    console.log('🔄 Setting userId:', value);
    this._userId = value;
    if (value) {
      console.log('🔄 Calling loadUserDetails immediately');
      this.loadUserDetails();
    }
  }

  get userId(): string {
    return this._userId;
  }

  user: UserDetails | null = null;
  error: string | null = null;

  isEditing: boolean = false;
  isSaving: boolean = false;
  originalUserData: any = null;

  // Profile image upload
  profileImageFile: File | null = null;
  profileImagePreview: string | null = null;

  // Tab navigation
  activeTab: string = 'overview';

  // CPD Hours data
  cpdHoursList: CpdHoursResponse[] = [];
  isLoadingCpdHours = false;
  cpdHoursError: string | null = null;

  // Company data
  companyList: any[] = [];
  isLoadingCompanies = false;

  // Expose Math to template
  Math = Math;

  // Province data for cascading dropdowns
  provinceList: any[] = provinceData;
  
  // Registered address dropdowns
  registeredDistrictList: any[] = [];
  registeredSubdistrictList: any[] = [];
  registeredZipCodeList: any[] = [];
  
  // Current address dropdowns
  currentDistrictList: any[] = [];
  currentSubdistrictList: any[] = [];
  currentZipCodeList: any[] = [];
  
  // Same as registered checkbox
  sameAsRegistered: boolean = false;

  private subscription = new Subscription();

  constructor(
    public activeModal: NgbActiveModal,
    private userService: UserService,
    private cdr: ChangeDetectorRef,
    private http: HttpClient
  ) { }

  ngOnInit(): void {
    console.log('🔄 UserDetailModal ngOnInit - userId:', this.userId);
    this.loadCompanies();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Set active tab
   */
  setActiveTab(tab: string): void {
    this.activeTab = tab;
    console.log('🔄 Active tab changed to:', tab);
  }

  loadUserDetails(): void {
    if (!this.userId) {
      console.warn('⚠️ No userId provided, cannot load user details');
      return;
    }

    console.log('🔄 Loading user details for userId:', this.userId);
    this.error = null;
    this.user = null; // Reset previous data

    const sub = this.userService.getUserById(this.userId).subscribe({
      next: (user) => {
        console.log('✅ User details loaded successfully:', user);
        this.user = user;
        console.log('🔄 User data set, this.user:', this.user);
        console.log('🔍 User userInformation:', this.user?.userInformation);

        // Load CPD hours after user details are loaded
        this.loadCpdHours();

        // Force change detection
        this.cdr.detectChanges();
        console.log('🔄 Change detection triggered');
      },
      error: (error) => {
        console.error('❌ Error loading user details:', error);
        this.error = 'เกิดข้อผิดพลาดในการโหลดข้อมูลผู้ใช้งาน';

        // Force change detection
        this.cdr.detectChanges();
      }
    });

    this.subscription.add(sub);
  }

  /**
   * Load CPD hours from API
   */
  loadCpdHours(): void {
    if (!this.userId) {
      console.warn('⚠️ No userId provided, cannot load CPD hours');
      return;
    }

    console.log('🔄 Loading CPD hours for userId:', this.userId);
    this.isLoadingCpdHours = true;
    this.cpdHoursError = null;
    this.cpdHoursList = [];

    const apiUrl = `${environment.apiUrl}/cpd-hours/user/${this.userId}`;
    console.log('🔄 Fetching CPD hours from:', apiUrl);

    const sub = this.http.get<CpdHoursResponse[]>(apiUrl).subscribe({
      next: (response) => {
        console.log('✅ CPD hours loaded successfully:', response);
        this.cpdHoursList = response;
        this.isLoadingCpdHours = false;

        // Force change detection
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('❌ Error loading CPD hours:', error);
        this.cpdHoursError = 'เกิดข้อผิดพลาดในการโหลดข้อมูล CPD Hours';
        this.isLoadingCpdHours = false;

        // Force change detection
        this.cdr.detectChanges();
      }
    });

    this.subscription.add(sub);
  }

  /**
   * Parse number from string or number value
   */
  private parseNumber(value: number | string | undefined): number {
    if (value === undefined || value === null) return 0;
    if (typeof value === 'number') return value;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  }

  /**
   * Get completed courses count from CPD hours
   */
  getMockCompletedCourses(): number {
    return this.cpdHoursList.filter(cpd => cpd.isVerified).length;
  }

  /**
   * Get in-progress courses count from CPD hours
   */
  getMockInProgressCourses(): number {
    return this.cpdHoursList.filter(cpd => !cpd.isVerified && cpd.cpdPaymentStatus.name !== 'รอชำระเงิน').length;
  }

  /**
   * Get upcoming courses count (registered but not paid)
   */
  getMockUpcomingCourses(): number {
    return this.cpdHoursList.filter(cpd => cpd.cpdPaymentStatus.name === 'รอชำระเงิน').length;
  }

  /**
   * Get CPD card class based on value
   */
  getCPDCardClass(cpd: number | string): string {
    const cpdValue = this.parseNumber(cpd);
    if (cpdValue >= 20) return 'bg-success';
    if (cpdValue >= 10) return 'bg-warning';
    return 'bg-danger';
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string | undefined): string {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  /**
   * ข้อความวันหมดอายุบัตรสมาชิก (ตลอดชีพจะแสดงคำว่า "ตลอดชีพ")
   */
  getVATExpiryText(): string {
    return formatMemberExpiry(
      this.user?.userInformation?.vatType,
      this.user?.userInformation?.vatExpired,
      date => this.formatDate(date)
    );
  }

  /**
   * Convert ISO date to dd/mm/yyyy Buddhist Era format for display
   */
  convertISOtoBE(isoDate: string): string {
    if (!isoDate) return '';
    
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return '';
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const yearBE = date.getFullYear() + 543;
    
    return `${day}/${month}/${yearBE}`;
  }

  /**
   * Convert dd/mm/yyyy Buddhist Era to ISO format for storage
   */
  convertBEtoISO(beDate: string): string {
    if (!beDate) return '';
    
    const datePattern = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = beDate.match(datePattern);
    
    if (!match) return '';
    
    const day = match[1];
    const month = match[2];
    const yearBE = parseInt(match[3]);
    const yearCE = yearBE - 543;
    
    return `${yearCE}-${month}-${day}`;
  }

  /**
   * Handle birth date input with auto-formatting (dd/mm/yyyy BE)
   */
  onBirthDateInput(event: any): void {
    let value = event.target.value.replace(/[^0-9]/g, '');
    
    if (value.length >= 2) {
      value = value.substring(0, 2) + '/' + value.substring(2);
    }
    if (value.length >= 5) {
      value = value.substring(0, 5) + '/' + value.substring(5);
    }
    if (value.length > 10) {
      value = value.substring(0, 10);
    }
    
    event.target.value = value;
    if (this.user?.userInformation) {
      this.user.userInformation.dateOfBirth = value;
    }
  }

  /**
   * Handle training date input with auto-formatting (dd/mm/yyyy BE)
   */
  onTrainingDateInput(event: any, index: number): void {
    let value = event.target.value.replace(/[^0-9]/g, '');
    
    if (value.length >= 2) {
      value = value.substring(0, 2) + '/' + value.substring(2);
    }
    if (value.length >= 5) {
      value = value.substring(0, 5) + '/' + value.substring(5);
    }
    if (value.length > 10) {
      value = value.substring(0, 10);
    }
    
    event.target.value = value;
    if (this.user?.userInformation?.specialCourses && this.user.userInformation.specialCourses[index]) {
      this.user.userInformation.specialCourses[index].trainingDate = value;
    }
  }

  /**
   * Handle VAT expiry date input with auto-formatting (dd/mm/yyyy BE)
   */
  onVATExpiryDateInput(event: any): void {
    let value = event.target.value.replace(/[^0-9]/g, '');
    
    if (value.length >= 2) {
      value = value.substring(0, 2) + '/' + value.substring(2);
    }
    if (value.length >= 5) {
      value = value.substring(0, 5) + '/' + value.substring(5);
    }
    if (value.length > 10) {
      value = value.substring(0, 10);
    }
    
    event.target.value = value;
    if (this.user?.userInformation) {
      this.user.userInformation.vatExpired = value;
    }
  }

  /**
   * Handle Valuer expiry date input with auto-formatting (dd/mm/yyyy BE)
   */
  onValuerExpiryDateInput(event: any): void {
    let value = event.target.value.replace(/[^0-9]/g, '');
    
    if (value.length >= 2) {
      value = value.substring(0, 2) + '/' + value.substring(2);
    }
    if (value.length >= 5) {
      value = value.substring(0, 5) + '/' + value.substring(5);
    }
    if (value.length > 10) {
      value = value.substring(0, 10);
    }
    
    event.target.value = value;
    if (this.user?.userInformation) {
      this.user.userInformation.valuerExpired = value;
    }
  }

  /**
   * Handle registered province selection
   */
  onRegisteredProvinceChange(event: any): void {
    const provinceName = event.target.value;
    
    // Reset dependent fields
    if (this.user?.userInformation) {
      (this.user.userInformation as any).registeredDistrict = '';
      (this.user.userInformation as any).registeredSubdistrict = '';
      (this.user.userInformation as any).registeredPostalCode = '';
    }
    
    this.registeredDistrictList = [];
    this.registeredSubdistrictList = [];
    this.registeredZipCodeList = [];
    
    if (provinceName) {
      const selectedProvince = this.provinceList.find(p => p.name_th === provinceName);
      if (selectedProvince && selectedProvince.districts) {
        this.registeredDistrictList = selectedProvince.districts;
      }
    }
    
    // Copy to current address if checkbox is checked
    if (this.sameAsRegistered) {
      this.copyRegisteredToCurrent();
    }
  }

  /**
   * Handle registered district selection
   */
  onRegisteredDistrictChange(event: any): void {
    const districtName = event.target.value;
    
    // Reset dependent fields
    if (this.user?.userInformation) {
      (this.user.userInformation as any).registeredSubdistrict = '';
      (this.user.userInformation as any).registeredPostalCode = '';
    }
    
    this.registeredSubdistrictList = [];
    this.registeredZipCodeList = [];
    
    if (districtName) {
      const selectedDistrict = this.registeredDistrictList.find(d => d.name_th === districtName);
      if (selectedDistrict && selectedDistrict.sub_districts) {
        this.registeredSubdistrictList = selectedDistrict.sub_districts;
      }
    }
    
    // Copy to current address if checkbox is checked
    if (this.sameAsRegistered) {
      this.copyRegisteredToCurrent();
    }
  }

  /**
   * Handle registered subdistrict selection
   */
  onRegisteredSubdistrictChange(event: any): void {
    const subdistrictName = event.target.value;
    
    // Reset postal code
    if (this.user?.userInformation) {
      (this.user.userInformation as any).registeredPostalCode = '';
    }
    
    this.registeredZipCodeList = [];
    
    if (subdistrictName) {
      const selectedSubdistrict = this.registeredSubdistrictList.find(s => s.name_th === subdistrictName);
      if (selectedSubdistrict) {
        // Get unique zip codes for this subdistrict
        const zipCodes = [selectedSubdistrict.zip_code];
        this.registeredZipCodeList = [...new Set(zipCodes)].map(zip => ({ code: zip }));
        
        // Auto-select if only one zip code
        if (this.registeredZipCodeList.length === 1 && this.user?.userInformation) {
          (this.user.userInformation as any).registeredPostalCode = this.registeredZipCodeList[0].code.toString();
        }
      }
    }
    
    // Copy to current address if checkbox is checked
    if (this.sameAsRegistered) {
      this.copyRegisteredToCurrent();
    }
  }

  /**
   * Handle current province selection
   */
  onCurrentProvinceChange(event: any): void {
    const provinceName = event.target.value;
    
    // Reset dependent fields
    if (this.user?.userInformation) {
      (this.user.userInformation as any).currentDistrict = '';
      (this.user.userInformation as any).currentSubdistrict = '';
      (this.user.userInformation as any).currentPostalCode = '';
    }
    
    this.currentDistrictList = [];
    this.currentSubdistrictList = [];
    this.currentZipCodeList = [];
    
    if (provinceName) {
      const selectedProvince = this.provinceList.find(p => p.name_th === provinceName);
      if (selectedProvince && selectedProvince.districts) {
        this.currentDistrictList = selectedProvince.districts;
      }
    }
  }

  /**
   * Handle current district selection
   */
  onCurrentDistrictChange(event: any): void {
    const districtName = event.target.value;
    
    // Reset dependent fields
    if (this.user?.userInformation) {
      (this.user.userInformation as any).currentSubdistrict = '';
      (this.user.userInformation as any).currentPostalCode = '';
    }
    
    this.currentSubdistrictList = [];
    this.currentZipCodeList = [];
    
    if (districtName) {
      const selectedDistrict = this.currentDistrictList.find(d => d.name_th === districtName);
      if (selectedDistrict && selectedDistrict.sub_districts) {
        this.currentSubdistrictList = selectedDistrict.sub_districts;
      }
    }
  }

  /**
   * Handle current subdistrict selection
   */
  onCurrentSubdistrictChange(event: any): void {
    const subdistrictName = event.target.value;
    
    // Reset postal code
    if (this.user?.userInformation) {
      (this.user.userInformation as any).currentPostalCode = '';
    }
    
    this.currentZipCodeList = [];
    
    if (subdistrictName) {
      const selectedSubdistrict = this.currentSubdistrictList.find(s => s.name_th === subdistrictName);
      if (selectedSubdistrict) {
        // Get unique zip codes for this subdistrict
        const zipCodes = [selectedSubdistrict.zip_code];
        this.currentZipCodeList = [...new Set(zipCodes)].map(zip => ({ code: zip }));
        
        // Auto-select if only one zip code
        if (this.currentZipCodeList.length === 1 && this.user?.userInformation) {
          (this.user.userInformation as any).currentPostalCode = this.currentZipCodeList[0].code.toString();
        }
      }
    }
  }

  /**
   * Handle same as registered checkbox change
   */
  onSameAsRegisteredChange(event: any): void {
    this.sameAsRegistered = event.target.checked;

    if (this.sameAsRegistered) {
      this.copyRegisteredToCurrent();
      this.toggleCurrentAddressFields(false);
    } else {
      this.toggleCurrentAddressFields(true);
    }
  }

  /**
   * Copy registered address to current address
   */
  private copyRegisteredToCurrent(): void {
    if (!this.user?.userInformation) return;

    (this.user.userInformation as any).currentAddress = (this.user.userInformation as any).registeredAddress;
    (this.user.userInformation as any).currentProvince = (this.user.userInformation as any).registeredProvince;
    (this.user.userInformation as any).currentDistrict = (this.user.userInformation as any).registeredDistrict;
    (this.user.userInformation as any).currentSubdistrict = (this.user.userInformation as any).registeredSubdistrict;
    (this.user.userInformation as any).currentPostalCode = (this.user.userInformation as any).registeredPostalCode;

    // Copy dropdown lists
    this.currentDistrictList = [...this.registeredDistrictList];
    this.currentSubdistrictList = [...this.registeredSubdistrictList];
    this.currentZipCodeList = [...this.registeredZipCodeList];
  }

  /**
   * Toggle current address fields enabled/disabled
   */
  private toggleCurrentAddressFields(enabled: boolean): void {
    // This is handled by the [disabled] binding in the template
    // No need to manually enable/disable fields here
  }

  /**
   * Initialize address dropdowns when entering edit mode
   */
  initializeAddressDropdowns(): void {
    if (!this.user?.userInformation) return;
    
    // Initialize registered address dropdowns
    const registeredProvince = (this.user.userInformation as any).registeredProvince || this.user.userInformation.province;
    const registeredDistrict = (this.user.userInformation as any).registeredDistrict || this.user.userInformation.district;
    const registeredSubdistrict = (this.user.userInformation as any).registeredSubdistrict || this.user.userInformation.subdistrict;
    
    // Load registered districts for selected province
    if (registeredProvince) {
      const selectedProvince = this.provinceList.find(p => p.name_th === registeredProvince);
      if (selectedProvince && selectedProvince.districts) {
        this.registeredDistrictList = selectedProvince.districts;
      }
    }
    
    // Load registered subdistricts for selected district
    if (registeredDistrict && this.registeredDistrictList.length > 0) {
      const selectedDistrict = this.registeredDistrictList.find(d => d.name_th === registeredDistrict);
      if (selectedDistrict && selectedDistrict.sub_districts) {
        this.registeredSubdistrictList = selectedDistrict.sub_districts;
      }
    }
    
    // Load registered zip codes for selected subdistrict
    if (registeredSubdistrict && this.registeredSubdistrictList.length > 0) {
      const selectedSubdistrict = this.registeredSubdistrictList.find(s => s.name_th === registeredSubdistrict);
      if (selectedSubdistrict) {
        const zipCodes = [selectedSubdistrict.zip_code];
        this.registeredZipCodeList = [...new Set(zipCodes)].map(zip => ({ code: zip }));
      }
    }
    
    // Initialize current address dropdowns
    const currentProvince = (this.user.userInformation as any).currentProvince || this.user.userInformation.province;
    const currentDistrict = (this.user.userInformation as any).currentDistrict || this.user.userInformation.district;
    const currentSubdistrict = (this.user.userInformation as any).currentSubdistrict || this.user.userInformation.subdistrict;
    
    // Load current districts for selected province
    if (currentProvince) {
      const selectedProvince = this.provinceList.find(p => p.name_th === currentProvince);
      if (selectedProvince && selectedProvince.districts) {
        this.currentDistrictList = selectedProvince.districts;
      }
    }
    
    // Load current subdistricts for selected district
    if (currentDistrict && this.currentDistrictList.length > 0) {
      const selectedDistrict = this.currentDistrictList.find(d => d.name_th === currentDistrict);
      if (selectedDistrict && selectedDistrict.sub_districts) {
        this.currentSubdistrictList = selectedDistrict.sub_districts;
      }
    }
    
    // Load current zip codes for selected subdistrict
    if (currentSubdistrict && this.currentSubdistrictList.length > 0) {
      const selectedSubdistrict = this.currentSubdistrictList.find(s => s.name_th === currentSubdistrict);
      if (selectedSubdistrict) {
        const zipCodes = [selectedSubdistrict.zip_code];
        this.currentZipCodeList = [...new Set(zipCodes)].map(zip => ({ code: zip }));
      }
    }
    
    // Migrate old single address to registered address if dual addresses don't exist
    if (!(this.user.userInformation as any).registeredAddress && this.user.userInformation.address) {
      (this.user.userInformation as any).registeredAddress = this.user.userInformation.address;
      (this.user.userInformation as any).registeredProvince = this.user.userInformation.province;
      (this.user.userInformation as any).registeredDistrict = this.user.userInformation.district;
      (this.user.userInformation as any).registeredSubdistrict = this.user.userInformation.subdistrict;
      (this.user.userInformation as any).registeredPostalCode = this.user.userInformation.postalCode;
      
      // Also set current address to same values initially
      (this.user.userInformation as any).currentAddress = this.user.userInformation.address;
      (this.user.userInformation as any).currentProvince = this.user.userInformation.province;
      (this.user.userInformation as any).currentDistrict = this.user.userInformation.district;
      (this.user.userInformation as any).currentSubdistrict = this.user.userInformation.subdistrict;
      (this.user.userInformation as any).currentPostalCode = this.user.userInformation.postalCode;
    }
  }

  /**
   * Format phone number
   */
  formatPhoneNumber(phone: string | undefined): string {
    if (!phone) return '-';
    // Format as xxx-xxx-xxxx
    return phone.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
  }

  /**
   * Get expiration status for VAT
   */
  getVATExpirationStatus(): 'valid' | 'expiring-warning' | 'expiring-soon' | 'expired' | '' {
    // สมาชิกตลอดชีพไม่มีวันหมดอายุ ถือว่าใช้งานได้เสมอ
    if (isLifetimeMemberType(this.user?.userInformation?.vatType)) return 'valid';

    if (!this.user?.userInformation?.vatExpired) return '';

    const expiryDate = new Date(this.user.userInformation?.vatExpired);
    const today = new Date();
    const thirtyDaysFromNow = new Date(today.getTime() + (30 * 24 * 60 * 60 * 1000));
    const ninetyDaysFromNow = new Date(today.getTime() + (90 * 24 * 60 * 60 * 1000));

    if (expiryDate < today) return 'expired';
    if (expiryDate < thirtyDaysFromNow) return 'expiring-soon';
    if (expiryDate < ninetyDaysFromNow) return 'expiring-warning';
    return 'valid';
  }

  /**
   * Get expiration status for Valuer
   */
  getValuerExpirationStatus(): 'valid' | 'expiring-warning' | 'expiring-soon' | 'expired' | '' {
    if (!this.user?.userInformation?.valuerExpired) return '';

    const expiryDate = new Date(this.user.userInformation?.valuerExpired);
    const today = new Date();
    const thirtyDaysFromNow = new Date(today.getTime() + (30 * 24 * 60 * 60 * 1000));
    const ninetyDaysFromNow = new Date(today.getTime() + (90 * 24 * 60 * 60 * 1000));

    if (expiryDate < today) return 'expired';
    if (expiryDate < thirtyDaysFromNow) return 'expiring-soon';
    if (expiryDate < ninetyDaysFromNow) return 'expiring-warning';
    return 'valid';
  }

  /**
   * Get badge class for status
   */
  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'valid': return 'bg-success';
      case 'expiring-warning': return 'bg-warning';
      case 'expiring-soon': return 'bg-danger';
      case 'expired': return 'bg-danger';
      default: return 'bg-secondary';
    }
  }

  /**
   * Get CPD badge class based on value
   */
  getCPDBadgeClass(cpd: number | string): string {
    const cpdValue = this.parseNumber(cpd);
    if (cpdValue >= 20) return 'bg-success';
    if (cpdValue >= 10) return 'bg-warning';
    return 'bg-danger';
  }

  /**
   * Get expiration text for status
   */
  getExpirationText(status: string): string {
    switch (status) {
      case 'valid': return 'ใบอนุญาตยังใช้ได้';
      case 'expiring-warning': return 'ใกล้หมดอายุในอีก 3 เดือน';
      case 'expiring-soon': return 'ใกล้หมดอายุในอีก 1 เดือน';
      case 'expired': return 'ใบอนุญาตหมดอายุแล้ว';
      default: return 'ไม่มีข้อมูลวันหมดอายุ';
    }
  }

  /**
   * Export user data as PDF using HTML-to-Canvas approach
   */
  exportUserData(): void {
    if (!this.user) return;

    try {
      // Create HTML content
      const htmlContent = this.generateReportHTML();

      // Create temporary div with the content
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.width = '800px';
      tempDiv.style.backgroundColor = 'white';
      tempDiv.style.padding = '20px';
      tempDiv.style.fontFamily = 'Sarabun, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

      document.body.appendChild(tempDiv);

      // Convert to canvas
      html2canvas(tempDiv, {
        width: 800,
        height: tempDiv.offsetHeight,
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      }).then(canvas => {
        // Remove temporary div
        document.body.removeChild(tempDiv);

        // Create PDF
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF();

        const imgWidth = 210; // A4 width in mm
        const pageHeight = 295; // A4 height in mm
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;

        let position = 0;

        // Add first page
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        // Add additional pages if needed
        while (heightLeft >= 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }

        // Save PDF
        const fileName = `user-report-${this.user?.userInformation?.firstName || 'unknown'}-${this.user?.userInformation?.lastName || 'user'}-${new Date().toISOString().split('T')[0]}.pdf`;
        pdf.save(fileName);

        console.log('✅ PDF exported successfully:', fileName);
      }).catch(error => {
        console.error('❌ Error generating canvas:', error);
        document.body.removeChild(tempDiv);
        alert('เกิดข้อผิดพลาดในการสร้าง PDF กรุณาลองใหม่อีกครั้ง');
      });

    } catch (error) {
      console.error('❌ Error generating PDF:', error);
      alert('เกิดข้อผิดพลาดในการสร้าง PDF กรุณาลองใหม่อีกครั้ง');
    }
  }

  /**
   * Generate HTML content for PDF report
   */
  private generateReportHTML(): string {
    const user = this.user!;
    const personalInfo = {
      firstName: user?.userInformation?.firstName || '',
      lastName: user?.userInformation?.lastName || '',
      email: user?.email || '',
      phone: user?.userInformation?.phoneNumber || '',
      cid: user?.cid || '',
      gender: user?.userInformation?.gender || '',
      dateOfBirth: user?.userInformation?.dateOfBirth || '',
      organization: user?.userInformation?.organization || ''
    };

    const professionalInfo = {
      role: user?.userRole?.role?.name || '',
      rankLevel: user?.userRank?.rank?.level || '',
      rankName: user?.userRank?.rank?.name || '',
      licenseType: user?.userLicense?.license?.name || ''
    };

    const cpdInfo = {
      vatCpd: this.parseNumber(user?.userInformation?.vatCpd),
      tvaCpd: this.parseNumber(user?.userInformation?.tvaCpd),
      otherCpd: this.parseNumber(user?.userInformation?.otherCpd),
      totalCpd: this.parseNumber(user?.userInformation?.totalCpd)
    };

    const licenseInfo = {
      vatId: user?.userInformation?.vatId || '',
      vatExpired: user?.userInformation?.vatExpired || '',
      valuerId: user?.userInformation?.valuerId || '',
      valuerExpired: user?.userInformation?.valuerExpired || ''
    };

    const userName = `${personalInfo.firstName} ${personalInfo.lastName}`.trim() || 'Unknown User';
    const statusText = user?.isActive ? 'ใช้งาน' : 'ไม่ใช้งาน';
    const statusColor = user?.isActive ? '#28a745' : '#6c757d';

    const totalCpd = cpdInfo.totalCpd;
    const progressPercent = Math.min((totalCpd / 20) * 100, 100);
    let progressColor = '#dc3545';
    if (progressPercent >= 100) progressColor = '#28a745';
    else if (progressPercent >= 50) progressColor = '#ffc107';

    const vatStatus = this.getExpirationText(this.getVATExpirationStatus());
    const vatStatusColor = this.getStatusColor(this.getVATExpirationStatus());

    const valuerStatus = this.getExpirationText(this.getValuerExpirationStatus());
    const valuerStatusColor = this.getStatusColor(this.getValuerExpirationStatus());

    return `
      <div style="max-width: 800px; margin: 0 auto; font-family: 'Sarabun', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #333;">
        <!-- Header -->
        <div style="background: linear-gradient(135deg, #007bff 0%, #0056b3 100%); color: white; padding: 20px; border-radius: 8px; margin-bottom: 20px;">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <div>
              <h1 style="margin: 0; font-size: 24px; font-weight: 700;">VAT Management System</h1>
              <p style="margin: 5px 0 0 0; opacity: 0.9;">รายงานข้อมูลผู้ใช้งาน</p>
            </div>
            <div style="text-align: right;">
              <div style="font-size: 12px; opacity: 0.9;">วันที่ส่งออก</div>
              <div style="font-size: 14px; font-weight: 600;">${new Date().toLocaleDateString('th-TH')}</div>
            </div>
          </div>
        </div>
        
        <!-- User Name and Status -->
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 25px; padding-bottom: 15px; border-bottom: 2px solid #dee2e6;">
          <div>
            <h2 style="margin: 0; color: #007bff; font-size: 20px; font-weight: 600;">${userName}</h2>
            <p style="margin: 5px 0 0 0; color: #6c757d;">${personalInfo.email || 'ไม่มีข้อมูลอีเมล'}</p>
          </div>
          <div style="text-align: right;">
            <span style="background-color: ${statusColor}; color: white; padding: 5px 15px; border-radius: 20px; font-size: 14px; font-weight: 500;">
              สถานะ: ${statusText}
            </span>
          </div>
        </div>
        
        <!-- Personal Information -->
        <div style="margin-bottom: 25px;">
          <h3 style="color: #007bff; font-size: 18px; margin-bottom: 15px; padding-bottom: 8px; border-bottom: 2px solid #007bff;">
            📋 ข้อมูลส่วนตัว
          </h3>
          <div style="background: #f8f9fa; padding: 20px; border-radius: 8px;">
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px;">
              <div>
                <div style="color: #6c757d; font-size: 12px; font-weight: 600; margin-bottom: 3px;">ชื่อ-นามสกุล</div>
                <div style="font-weight: 600;">${userName}</div>
              </div>
              <div>
                <div style="color: #6c757d; font-size: 12px; font-weight: 600; margin-bottom: 3px;">เลขบัตรประชาชน</div>
                <div style="font-family: monospace;">${personalInfo.cid || 'ไม่มีข้อมูล'}</div>
              </div>
              <div>
                <div style="color: #6c757d; font-size: 12px; font-weight: 600; margin-bottom: 3px;">เพศ</div>
                <div>${personalInfo.gender === 'Male' ? 'ชาย' : personalInfo.gender === 'Female' ? 'หญิง' : 'ไม่ระบุ'}</div>
              </div>
              <div>
                <div style="color: #6c757d; font-size: 12px; font-weight: 600; margin-bottom: 3px;">วันเกิด</div>
                <div>${this.formatDate(personalInfo.dateOfBirth)}</div>
              </div>
              <div>
                <div style="color: #6c757d; font-size: 12px; font-weight: 600; margin-bottom: 3px;">หมายเลขโทรศัพท์</div>
                <div>${this.formatPhoneNumber(personalInfo.phone)}</div>
              </div>
              <div>
                <div style="color: #6c757d; font-size: 12px; font-weight: 600; margin-bottom: 3px;">หน่วยงาน/สังกัด</div>
                <div style="font-weight: 600;">${personalInfo.organization || 'ไม่มีข้อมูล'}</div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Professional Information -->
        <div style="margin-bottom: 25px;">
          <h3 style="color: #28a745; font-size: 18px; margin-bottom: 15px; padding-bottom: 8px; border-bottom: 2px solid #28a745;">
            💼 ข้อมูลการทำงาน
          </h3>
          <div style="background: #f8f9fa; padding: 20px; border-radius: 8px;">
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px;">
              <div>
                <div style="color: #6c757d; font-size: 12px; font-weight: 600; margin-bottom: 3px;">บทบาท</div>
                <div style="font-weight: 600;">${professionalInfo.role || 'ไม่มีข้อมูล'}</div>
              </div>
              <div>
                <div style="color: #6c757d; font-size: 12px; font-weight: 600; margin-bottom: 3px;">ระดับตำแหน่ง</div>
                <div>
                  <span style="background-color: #17a2b8; color: white; padding: 2px 8px; border-radius: 12px; font-size: 12px; margin-right: 8px;">
                    ${professionalInfo.rankLevel || 'N/A'}
                  </span>
                  ${professionalInfo.rankName || 'ไม่มีข้อมูล'}
                </div>
              </div>
              <div style="grid-column: 1 / -1;">
                <div style="color: #6c757d; font-size: 12px; font-weight: 600; margin-bottom: 3px;">ประเภทใบอนุญาต</div>
                <div style="font-weight: 600;">${professionalInfo.licenseType || 'ไม่มีข้อมูล'}</div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- CPD Information -->
        <div style="margin-bottom: 25px;">
          <h3 style="color: #ffc107; font-size: 18px; margin-bottom: 15px; padding-bottom: 8px; border-bottom: 2px solid #ffc107;">
            🏆 ข้อมูล CPD (Continuing Professional Development)
          </h3>
          
          <!-- CPD Summary Cards -->
          <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin-bottom: 20px;">
            <div style="background: linear-gradient(135deg, #007bff 0%, #0056b3 100%); color: white; padding: 15px; border-radius: 8px; text-align: center;">
              <div style="font-size: 24px; font-weight: 700; margin-bottom: 5px;">${cpdInfo.vatCpd}</div>
              <div style="font-size: 12px; opacity: 0.9;">VAT CPD ชั่วโมง</div>
            </div>
            <div style="background: linear-gradient(135deg, #28a745 0%, #1e7e34 100%); color: white; padding: 15px; border-radius: 8px; text-align: center;">
              <div style="font-size: 24px; font-weight: 700; margin-bottom: 5px;">${cpdInfo.tvaCpd}</div>
              <div style="font-size: 12px; opacity: 0.9;">TVA CPD ชั่วโมง</div>
            </div>
            <div style="background: linear-gradient(135deg, #17a2b8 0%, #117a8b 100%); color: white; padding: 15px; border-radius: 8px; text-align: center;">
              <div style="font-size: 24px; font-weight: 700; margin-bottom: 5px;">${cpdInfo.otherCpd}</div>
              <div style="font-size: 12px; opacity: 0.9;">CPD อื่นๆ ชั่วโมง</div>
            </div>
            <div style="background: ${progressColor}; color: white; padding: 15px; border-radius: 8px; text-align: center;">
              <div style="font-size: 24px; font-weight: 700; margin-bottom: 5px;">${cpdInfo.totalCpd}</div>
              <div style="font-size: 12px; opacity: 0.9;">รวมทั้งหมด ชั่วโมง</div>
            </div>
          </div>
          
          <!-- Progress Bar -->
          <div style="background: white; padding: 20px; border-radius: 8px; border: 1px solid #dee2e6;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
              <div style="font-weight: 600;">ความคืบหน้า CPD ประจำปี</div>
              <div style="color: #6c757d; font-size: 14px;">เป้าหมาย 20 ชั่วโมง/ปี</div>
            </div>
            <div style="background-color: #e9ecef; height: 20px; border-radius: 10px; overflow: hidden; margin-bottom: 10px;">
              <div style="background-color: ${progressColor}; height: 100%; width: ${progressPercent}%; border-radius: 10px; transition: width 0.3s ease;"></div>
            </div>
            <div style="display: flex; justify-content: space-between; font-size: 14px; color: #6c757d;">
              <span>${progressPercent.toFixed(1)}% ของเป้าหมาย</span>
              <span>เหลืออีก ${Math.max(20 - totalCpd, 0)} ชั่วโมง</span>
            </div>
          </div>
        </div>
        
        <!-- License Information -->
        <div style="margin-bottom: 25px;">
          <h3 style="color: #dc3545; font-size: 18px; margin-bottom: 15px; padding-bottom: 8px; border-bottom: 2px solid #dc3545;">
            🏅 ข้อมูลใบอนุญาต
          </h3>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px;">
            <!-- VAT License -->
            <div style="background: white; border: 1px solid #dee2e6; border-radius: 8px; overflow: hidden;">
              <div style="background: linear-gradient(135deg, #007bff 0%, #0056b3 100%); color: white; padding: 15px; text-align: center;">
                <div style="font-size: 18px; font-weight: 600; margin-bottom: 5px;">ใบอนุญาต VAT</div>
                <div style="font-size: 12px; opacity: 0.9;">Value Added Tax License</div>
              </div>
              <div style="padding: 20px;">
                <div style="margin-bottom: 15px;">
                  <div style="color: #6c757d; font-size: 12px; font-weight: 600; margin-bottom: 3px;">หมายเลข VAT ID</div>
                  <div style="font-family: monospace; font-weight: 700; font-size: 16px;">${licenseInfo.vatId || 'ไม่มีข้อมูล'}</div>
                </div>
                <div style="margin-bottom: 15px;">
                  <div style="color: #6c757d; font-size: 12px; font-weight: 600; margin-bottom: 3px;">วันหมดอายุ</div>
                  <div style="font-weight: 600;">${this.formatDate(licenseInfo.vatExpired)}</div>
                </div>
                <div style="background-color: #f8f9fa; padding: 10px; border-radius: 6px; display: flex; align-items: center;">
                  <div style="width: 8px; height: 8px; border-radius: 50%; background-color: ${vatStatusColor}; margin-right: 8px;"></div>
                  <div style="font-size: 14px; font-weight: 600; color: ${vatStatusColor};">${vatStatus}</div>
                </div>
              </div>
            </div>
            
            <!-- Valuer License -->
            <div style="background: white; border: 1px solid #dee2e6; border-radius: 8px; overflow: hidden;">
              <div style="background: linear-gradient(135deg, #28a745 0%, #1e7e34 100%); color: white; padding: 15px; text-align: center;">
                <div style="font-size: 18px; font-weight: 600; margin-bottom: 5px;">ใบอนุญาต Valuer</div>
                <div style="font-size: 12px; opacity: 0.9;">Professional Valuer License</div>
              </div>
              <div style="padding: 20px;">
                <div style="margin-bottom: 15px;">
                  <div style="color: #6c757d; font-size: 12px; font-weight: 600; margin-bottom: 3px;">หมายเลข Valuer ID</div>
                  <div style="font-family: monospace; font-weight: 700; font-size: 16px;">${licenseInfo.valuerId || 'ไม่มีข้อมูล'}</div>
                </div>
                <div style="margin-bottom: 15px;">
                  <div style="color: #6c757d; font-size: 12px; font-weight: 600; margin-bottom: 3px;">วันหมดอายุ</div>
                  <div style="font-weight: 600;">${this.formatDate(licenseInfo.valuerExpired)}</div>
                </div>
                <div style="background-color: #f8f9fa; padding: 10px; border-radius: 6px; display: flex; align-items: center;">
                  <div style="width: 8px; height: 8px; border-radius: 50%; background-color: ${valuerStatusColor}; margin-right: 8px;"></div>
                  <div style="font-size: 14px; font-weight: 600; color: ${valuerStatusColor};">${valuerStatus}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Footer -->
        <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #dee2e6; text-align: center; color: #6c757d; font-size: 12px;">
          <div>สร้างโดย VAT Management System | ระบบจัดการภาษีมูลค่าเพิ่ม</div>
          <div style="margin-top: 5px;">รายงานนี้สร้างขึ้นในวันที่ ${new Date().toLocaleDateString('th-TH')} เวลา ${new Date().toLocaleTimeString('th-TH')}</div>
        </div>
      </div>
    `;
  }

  /**
   * Get status color for PDF as RGB array
   */
  private getStatusColor(status: string): string {
    switch (status) {
      case 'valid': return '#28a745'; // Green
      case 'expiring-warning': return '#ffc107'; // Yellow
      case 'expiring-soon': return '#fd7e14'; // Orange
      case 'expired': return '#dc3545'; // Red
      default: return '#6c757d'; // Gray
    }
  }

  /**
   * Get total CPD hours as number
   */
  getTotalCpdHours(): number {
    // คำนวณจากคอร์สที่อนุมัติ/ตรวจสอบแล้วจริง (cpdHoursList) แทนค่าคงที่ totalCpd ที่อาจไม่อัปเดต
    if (this.cpdHoursList && this.cpdHoursList.length > 0) {
      return this.cpdHoursList
        .filter(cpd => cpd.isVerified)
        .reduce((sum, cpd) => sum + this.parseNumber(cpd.hoursStudied), 0);
    }
    return this.parseNumber(this.user?.userInformation?.totalCpd);
  }

  /**
   * Get VAT CPD hours as number
   */
  getVatCpdHours(): number {
    return this.parseNumber(this.user?.userInformation?.vatCpd);
  }

  /**
   * Get TVA CPD hours as number
   */
  getTvaCpdHours(): number {
    return this.parseNumber(this.user?.userInformation?.tvaCpd);
  }

  /**
   * Get Other CPD hours as number
   */
  getOtherCpdHours(): number {
    return this.parseNumber(this.user?.userInformation?.otherCpd);
  }

  /**
   * Get CPD progress percentage
   */
  getCpdProgressPercent(): number {
    const totalCpd = this.getTotalCpdHours();
    return Math.min((totalCpd / 20) * 100, 100);
  }

  /**
   * Get remaining CPD hours
   */
  getRemainingCpdHours(): number {
    const totalCpd = this.getTotalCpdHours();
    return Math.max(20 - totalCpd, 0);
  }

  /**
   * Get CPD type badge class
   */
  getCpdTypeBadgeClass(category: string): string {
    const categoryLower = category.toLowerCase();
    if (categoryLower.includes('accounting') || categoryLower.includes('vat')) return 'bg-success';
    if (categoryLower.includes('valuation') || categoryLower.includes('property')) return 'bg-success';
    if (categoryLower.includes('management')) return 'bg-warning';
    return 'bg-warning';
  }

  /**
   * Get CPD type label
   */
  getCpdTypeLabel(category: string): string {
    const categoryLower = category.toLowerCase();
    if (categoryLower.includes('accounting') || categoryLower.includes('vat')) return 'VAT CPD';
    if (categoryLower.includes('valuation') || categoryLower.includes('property')) return 'TVA CPD';
    return 'อื่นๆ CPD';
  }

  /**
   * Get course status class
   */
  getCourseStatusClass(cpd: CpdHoursResponse): string {
    if (cpd.isVerified) return 'completed';
    if (cpd.cpdPaymentStatus.name === 'รอชำระเงิน') return 'upcoming';
    return 'in-progress';
  }

  /**
   * Get course status icon
   */
  getCourseStatusIcon(cpd: CpdHoursResponse): string {
    if (cpd.isVerified) return 'bi-check-circle-fill';
    if (cpd.cpdPaymentStatus.name === 'รอชำระเงิน') return 'bi-calendar-plus';
    return 'bi-play-circle-fill';
  }

  /**
   * Get course status text
   */
  getCourseStatusText(cpd: CpdHoursResponse): string {
    if (cpd.isVerified) return this.formatDate(cpd.studyDate);
    if (cpd.cpdPaymentStatus.name === 'รอชำระเงิน') return 'รอชำระเงิน';
    return 'กำลังเรียน';
  }

  toggleEdit(): void {
    if (!this.isEditing) {
      // Entering edit mode - store original data
      this.originalUserData = JSON.parse(JSON.stringify(this.user));
      
      // Initialize objects if they don't exist
      if (this.user && !this.user.userInformation) {
        this.user.userInformation = {} as any;
      }
      if (this.user && !this.user.userLicense) {
        this.user.userLicense = {} as any;
      }
      
      // Initialize address dropdowns
      this.initializeAddressDropdowns();
      
      // Convert ISO date to BE format for editing
      if (this.user?.userInformation?.dateOfBirth) {
        const beDate = this.convertISOtoBE(this.user.userInformation.dateOfBirth);
        if (beDate) {
          this.user.userInformation.dateOfBirth = beDate;
        }
      }
      
      // Convert VAT expiry date to BE format
      if (this.user?.userInformation?.vatExpired) {
        const beDate = this.convertISOtoBE(this.user.userInformation.vatExpired);
        if (beDate) {
          this.user.userInformation.vatExpired = beDate;
        }
      }
      
      // Convert Valuer expiry date to BE format
      if (this.user?.userInformation?.valuerExpired) {
        const beDate = this.convertISOtoBE(this.user.userInformation.valuerExpired);
        if (beDate) {
          this.user.userInformation.valuerExpired = beDate;
        }
      }
    }
    this.isEditing = !this.isEditing;
  }

  cancelEdit(): void {
    if (this.originalUserData) {
      // Restore original data
      this.user = JSON.parse(JSON.stringify(this.originalUserData));
      this.originalUserData = null;
    }
    this.isEditing = false;
  }

  addEducationHistory(): void {
    if (this.user?.userInformation) {
      if (!this.user.userInformation.educationHistory) {
        this.user.userInformation.educationHistory = [];
      }
      this.user.userInformation.educationHistory.push({
        institution: '',
        'Degree and Major': '',
        'Year Complete': ''
      });
    }
  }

  removeEducationHistory(index: number): void {
    if (this.user?.userInformation?.educationHistory) {
      this.user.userInformation.educationHistory.splice(index, 1);
    }
  }

  addValuationExperience(): void {
    if (this.user?.userInformation) {
      if (!this.user.userInformation.valuationExperience) {
        this.user.userInformation.valuationExperience = [];
      }
      this.user.userInformation.valuationExperience.push({
        organization: '',
        position: '',
        years: ''
      });
    }
  }

  removeValuationExperience(index: number): void {
    if (this.user?.userInformation?.valuationExperience) {
      this.user.userInformation.valuationExperience.splice(index, 1);
    }
  }

  addSpecialCourse(): void {
    if (this.user?.userInformation) {
      if (!this.user.userInformation.specialCourses) {
        this.user.userInformation.specialCourses = [];
      }
      this.user.userInformation.specialCourses.push({
        courseTopic: '',
        organization: '',
        cpdHours: 0,
        trainingDate: '',
        status: ''
      });
    }
  }

  removeSpecialCourse(index: number): void {
    if (this.user?.userInformation?.specialCourses) {
      this.user.userInformation.specialCourses.splice(index, 1);
    }
  }

  /**
   * Handle profile image file selection
   */
  onProfileImageSelected(event: any): void {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      alert('กรุณาเลือกไฟล์รูปภาพ (JPG, PNG, GIF) เท่านั้น');
      event.target.value = '';
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('ขนาดไฟล์ต้องไม่เกิน 5MB');
      event.target.value = '';
      return;
    }

    this.profileImageFile = file;

    // Create preview
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.profileImagePreview = e.target.result;
      // Update user object for immediate preview
      if (this.user?.userInformation) {
        (this.user.userInformation as any).profileImage = e.target.result;
      }
    };
    reader.readAsDataURL(file);
  }

  /**
   * Remove profile image
   */
  removeProfileImage(): void {
    this.profileImageFile = null;
    this.profileImagePreview = null;
    if (this.user?.userInformation) {
      this.user.userInformation.profileImage = undefined;
      this.user.userInformation.profilePicture = undefined;
    }
  }

  /**
   * Trigger file input click
   */
  triggerProfileImageUpload(): void {
    const fileInput = document.getElementById('profileImageInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  loadCompanies(): void {
    this.isLoadingCompanies = true;
    this.http.get<any>(`${environment.apiUrl}/companies?page=1&limit=100`).subscribe({
      next: (response) => {
        this.companyList = response.data || [];
        this.isLoadingCompanies = false;
        console.log('🏢 Loaded companies:', this.companyList.length);
      },
      error: (error) => {
        console.error('❌ Error loading companies:', error);
        this.isLoadingCompanies = false;
      }
    });
  }

  onCompanyChange(): void {
    if (this.user?.userInformation) {
      const selectedId = this.user.userInformation.companyId;
      if (selectedId) {
        const found = this.companyList.find(c => c.id === selectedId);
        if (found) {
          this.user.userInformation.company = found;
        }
      } else {
        this.user.userInformation.company = null as any;
      }
    }
  }

  saveChanges(): void {
    if (!this.userId || !this.user) return;

    this.isSaving = true;

    // ตรวจ/บันทึกอีเมลก่อนเป็นอันดับแรก หากอีเมลซ้ำจะหยุดทันที ไม่บันทึกข้อมูลส่วนอื่น
    this.saveUserAccount(() => {
      // If profile image was uploaded, upload it first
      if (this.profileImageFile) {
        this.uploadProfileImage();
        return;
      }

      // Otherwise, proceed with normal save
      this.saveUserInformation();
    });
  }

  /**
   * Upload profile image to server
   */
  uploadProfileImage(): void {
    if (!this.profileImageFile || !this.userId) return;

    const formData = new FormData();
    formData.append('file', this.profileImageFile);
    formData.append('folder', 'profiles');

    const uploadUrl = `${environment.apiUrl}/upload/image`;

    console.log('📤 Uploading profile image:', this.profileImageFile.name);

    this.http.post(uploadUrl, formData).subscribe({
      next: (response: any) => {
        console.log('✅ Profile image uploaded successfully', response);
        
        // Update user object with new image URL
        // Handle response format: response.url or response.data.url
        let imageUrl = '';
        if (response && response.url) {
          imageUrl = response.url;
        } else if (response && response.data && response.data.url) {
          imageUrl = response.data.url;
        }

        if (this.user?.userInformation && imageUrl) {
          (this.user.userInformation as any).profileImage = imageUrl;
          this.user.userInformation.profilePicture = imageUrl;
        }

        // Clear upload state
        this.profileImageFile = null;
        this.profileImagePreview = null;

        // Continue with saving other user information
        this.saveUserInformation();
      },
      error: (error) => {
        console.error('❌ Error uploading profile image:', error);
        this.isSaving = false;
        alert('เกิดข้อผิดพลาดในการอัพโหลดรูปโปรไฟล์: ' + (error.error?.message || error.message));
      }
    });
  }

  /**
   * Save user information (separated from saveChanges for better flow)
   */
  saveUserInformation(): void {
    if (!this.userId || !this.user) return;

    // Prepare user information payload
    const userInfoPayload: any = this.user.userInformation ? { ...this.user.userInformation } : {};
    
    // Explicitly include dual address fields (they may be dynamic properties)
    const userInfo = this.user.userInformation as any;
    if (userInfo) {
      // Registered address fields
      if (userInfo.registeredAddress !== undefined) userInfoPayload.registeredAddress = userInfo.registeredAddress;
      if (userInfo.registeredProvince !== undefined) userInfoPayload.registeredProvince = userInfo.registeredProvince;
      if (userInfo.registeredDistrict !== undefined) userInfoPayload.registeredDistrict = userInfo.registeredDistrict;
      if (userInfo.registeredSubdistrict !== undefined) userInfoPayload.registeredSubdistrict = userInfo.registeredSubdistrict;
      if (userInfo.registeredPostalCode !== undefined) userInfoPayload.registeredPostalCode = userInfo.registeredPostalCode;
      
      // Current address fields
      if (userInfo.currentAddress !== undefined) userInfoPayload.currentAddress = userInfo.currentAddress;
      if (userInfo.currentProvince !== undefined) userInfoPayload.currentProvince = userInfo.currentProvince;
      if (userInfo.currentDistrict !== undefined) userInfoPayload.currentDistrict = userInfo.currentDistrict;
      if (userInfo.currentSubdistrict !== undefined) userInfoPayload.currentSubdistrict = userInfo.currentSubdistrict;
      if (userInfo.currentPostalCode !== undefined) userInfoPayload.currentPostalCode = userInfo.currentPostalCode;
      
      // IMPORTANT: Sync registered address to old single address fields for backward compatibility
      // The backend still uses address, province, district, subdistrict, postalCode
      if (userInfo.registeredAddress !== undefined) {
        userInfoPayload.address = userInfo.registeredAddress;
      }
      if (userInfo.registeredProvince !== undefined) {
        userInfoPayload.province = userInfo.registeredProvince;
      }
      if (userInfo.registeredDistrict !== undefined) {
        userInfoPayload.district = userInfo.registeredDistrict;
      }
      if (userInfo.registeredSubdistrict !== undefined) {
        userInfoPayload.subdistrict = userInfo.registeredSubdistrict;
      }
      if (userInfo.registeredPostalCode !== undefined) {
        userInfoPayload.postalCode = userInfo.registeredPostalCode;
      }
    }
    
    console.log('📦 Saving user information with dual addresses:', {
      registered: {
        address: userInfoPayload.registeredAddress,
        province: userInfoPayload.registeredProvince,
        district: userInfoPayload.registeredDistrict,
        subdistrict: userInfoPayload.registeredSubdistrict,
        postalCode: userInfoPayload.registeredPostalCode
      },
      current: {
        address: userInfoPayload.currentAddress,
        province: userInfoPayload.currentProvince,
        district: userInfoPayload.currentDistrict,
        subdistrict: userInfoPayload.currentSubdistrict,
        postalCode: userInfoPayload.currentPostalCode
      },
      oldFields: {
        address: userInfoPayload.address,
        province: userInfoPayload.province,
        district: userInfoPayload.district,
        subdistrict: userInfoPayload.subdistrict,
        postalCode: userInfoPayload.postalCode
      }
    });
    
    // Convert BE date to ISO format before saving
    if (userInfoPayload.dateOfBirth && userInfoPayload.dateOfBirth.includes('/')) {
      const isoDate = this.convertBEtoISO(userInfoPayload.dateOfBirth);
      if (isoDate) {
        userInfoPayload.dateOfBirth = isoDate;
      }
    }
    
    // Convert VAT expiry date to ISO format
    if (userInfoPayload.vatExpired && userInfoPayload.vatExpired.includes('/')) {
      const isoDate = this.convertBEtoISO(userInfoPayload.vatExpired);
      if (isoDate) {
        userInfoPayload.vatExpired = isoDate;
      }
    }
    
    // Convert Valuer expiry date to ISO format
    if (userInfoPayload.valuerExpired && userInfoPayload.valuerExpired.includes('/')) {
      const isoDate = this.convertBEtoISO(userInfoPayload.valuerExpired);
      if (isoDate) {
        userInfoPayload.valuerExpired = isoDate;
      }
    }

    const apiUrl = `${environment.apiUrl}/user-information/user/${this.userId}`;
    const payload: any = userInfoPayload;

    // Parse numeric fields to prevent database errors (e.g. invalid input syntax for type numeric)
    const numericFields = ['vatCpd', 'tvaCpd', 'otherCpd', 'totalCpd'];
    numericFields.forEach(field => {
      if (payload[field] !== undefined && payload[field] !== null) {
        if (typeof payload[field] === 'string') {
          // Remove commas or other weird formats if they exist, then parse
          const cleaned = payload[field].replace(/,/g, '');
          payload[field] = parseFloat(cleaned);
          if (isNaN(payload[field])) payload[field] = 0;
        }
      }
    });

    // Clean up any relational objects that shouldn't be sent in the PATCH request
    delete payload.company;
    delete payload.user;

    // Convert arrays back if they need specific mapping or standardizations
    if (payload.educationHistory) {
      // API expects standard naming on object props sometimes
      payload.educationHistory = payload.educationHistory.map((edu: any) => ({
        institution: edu.institution || edu.Institution || '',
        'Degree and Major': edu['Degree and Major'] || edu.degreeAndMajor || '',
        'Year Complete': edu['Year Complete'] || edu.yearComplete || ''
      }));
    }

    // Save user information first
    this.http.patch(apiUrl, payload).subscribe({
      next: (response) => {
        console.log('✅ User information updated successfully', response);

        // Save license information if it exists
        if (this.user?.userLicense && this.user.userLicense.id) {
          this.saveLicenseInfo();
        } else {
          this.finalizeSave();
        }
      },
      error: (error) => {
        console.error('❌ Error updating user info:', error);
        this.isSaving = false;
        alert('เกิดข้อผิดพลาดในการบันทึกข้อมูล');
      }
    });

  }

  /**
   * Save account-level fields (email) via PATCH /users/:id
   * จะบันทึกเฉพาะเมื่ออีเมลถูกแก้ไข และตรวจรูปแบบอีเมลก่อนส่ง
   */
  private saveUserAccount(onDone: () => void): void {
    if (!this.userId || !this.user) {
      onDone();
      return;
    }

    const newEmail = (this.user.email || '').trim();
    const oldEmail = (this.originalUserData?.email || '').trim();

    // ไม่มีการเปลี่ยนอีเมล -> ข้ามขั้นตอนนี้
    if (!newEmail || newEmail === oldEmail) {
      onDone();
      return;
    }

    // ตรวจรูปแบบอีเมลเบื้องต้น
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(newEmail)) {
      this.isSaving = false;
      alert('รูปแบบอีเมลไม่ถูกต้อง กรุณาตรวจสอบอีกครั้ง');
      return;
    }

    const apiUrl = `${environment.apiUrl}/users/${this.userId}`;
    this.http.patch(apiUrl, { email: newEmail }).subscribe({
      next: (response) => {
        console.log('✅ Email updated successfully', response);
        onDone();
      },
      error: (error) => {
        console.error('❌ Error updating email:', error);
        this.isSaving = false;
        const msg = error?.error?.message;
        const detail = Array.isArray(msg) ? msg.join(', ') : (msg || 'อีเมลอาจซ้ำกับผู้ใช้อื่น');
        alert('เกิดข้อผิดพลาดในการบันทึกอีเมล: ' + detail);
      }
    });
  }

  /**
   * Save license information
   */
  saveLicenseInfo(): void {
    if (!this.user?.userLicense?.id) {
      this.finalizeSave();
      return;
    }

    const licenseApiUrl = `${environment.apiUrl}/user-licenses/${this.user.userLicense.id}`;
    const licensePayload = {
      licenseNumber: this.user.userLicense.licenseNumber,
      expiresAt: this.user.userLicense.expiresAt
    };

    this.http.patch(licenseApiUrl, licensePayload).subscribe({
      next: (response) => {
        console.log('✅ License information updated successfully', response);
        this.finalizeSave();
      },
      error: (error) => {
        console.error('❌ Error updating license info:', error);
        // Continue anyway - user info was saved
        this.finalizeSave();
      }
    });
  }

  /**
   * Finalize save operation
   */
  finalizeSave(): void {
    this.isEditing = false;
    this.isSaving = false;
    this.originalUserData = null;
    alert('บันทึกข้อมูลสำเร็จ');
    this.loadUserDetails(); // Reload latest data
  }

  /**
   * Approve user - set userStatusId to 1
   */
  approveUser(): void {
    if (!this.userId) {
      console.warn('⚠️ No userId provided');
      return;
    }

    const confirmed = confirm('คุณต้องการอนุมัติผู้ใช้นี้หรือไม่?');
    if (!confirmed) return;

    console.log('🔄 Approving user:', this.userId);

    const apiUrl = `${environment.apiUrl}/user-information/user/${this.userId}`;
    const payload = {
      userStatusId: 1,
      userStatusNote: "",
      emailForApproved: true
    };

    const sub = this.http.patch(apiUrl, payload).subscribe({
      next: (response) => {
        console.log('✅ User approved successfully:', response);
        alert('อนุมัติผู้ใช้สำเร็จ');
        this.activeModal.close({ approved: true });
      },
      error: (error) => {
        console.error('❌ Error approving user:', error);
        alert('เกิดข้อผิดพลาดในการอนุมัติผู้ใช้');
      }
    });

    this.subscription.add(sub);
  }

  /**
   * Reject user - set userStatusId to 3 with reason
   */
  rejectUser(): void {
    if (!this.userId) {
      console.warn('⚠️ No userId provided');
      return;
    }

    const reason = prompt('กรุณาระบุเหตุผลที่ไม่อนุมัติ:');
    if (!reason || reason.trim() === '') {
      alert('กรุณาระบุเหตุผล');
      return;
    }

    console.log('🔄 Rejecting user:', this.userId, 'Reason:', reason);

    const apiUrl = `${environment.apiUrl}/user-information/user/${this.userId}`;
    const payload = {
      userStatusId: 3,
      userStatusNote: reason.trim()
    };

    const sub = this.http.patch(apiUrl, payload).subscribe({
      next: (response) => {
        console.log('✅ User rejected successfully:', response);
        alert('ไม่อนุมัติผู้ใช้สำเร็จ');
        this.activeModal.close({ rejected: true, reason });
      },
      error: (error) => {
        console.error('❌ Error rejecting user:', error);
        alert('เกิดข้อผิดพลาดในการไม่อนุมัติผู้ใช้');
      }
    });

    this.subscription.add(sub);
  }

  /**
   * Close modal
   */
  close(): void {
    this.activeModal.close();
  }

  /**
   * Dismiss modal
   */
  dismiss(): void {
    this.activeModal.dismiss();
  }

  /**
   * Detect if URL is a PDF file
   */
  private isPdfFile(url: string): boolean {
    if (!url) return false;
    const lowerUrl = url.toLowerCase();
    return lowerUrl.includes('.pdf') || lowerUrl.includes('application/pdf') || lowerUrl.includes('data:application/pdf');
  }

  /**
   * Open file preview (image or PDF) in a new window
   */
  openImagePreview(fileUrl: string, title: string): void {
    if (!fileUrl) return;

    const isPdf = this.isPdfFile(fileUrl);
    
    // For PDFs, open directly in new tab with PDF viewer
    if (isPdf) {
      window.open(fileUrl, '_blank');
      return;
    }

    // For images, create a styled preview window
    const previewWindow = window.open('', '_blank', 'width=900,height=700,resizable=yes,scrollbars=yes');

    if (previewWindow) {
      previewWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>${title || 'File Preview'}</title>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              margin: 0;
              padding: 20px;
              background-color: #f8f9fa;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: flex-start;
              min-height: 100vh;
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Sarabun", sans-serif;
            }
            .header {
              background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
              color: white;
              padding: 15px 20px;
              border-radius: 8px;
              margin-bottom: 20px;
              text-align: center;
              width: 100%;
              max-width: 900px;
              box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
            }
            .header h2 {
              margin: 0;
              font-size: 18px;
              font-weight: 600;
            }
            .content-container {
              background: white;
              padding: 20px;
              border-radius: 8px;
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
              max-width: 100%;
              width: 100%;
              max-width: 900px;
            }
            img {
              max-width: 100%;
              height: auto;
              display: block;
              border-radius: 4px;
              margin: 0 auto;
            }
            iframe {
              width: 100%;
              height: 600px;
              border: none;
              border-radius: 4px;
            }
            .button-group {
              margin-top: 20px;
              display: flex;
              gap: 10px;
              justify-content: center;
              flex-wrap: wrap;
            }
            .btn {
              padding: 10px 30px;
              border: none;
              border-radius: 4px;
              cursor: pointer;
              font-size: 14px;
              font-weight: 600;
              transition: all 0.3s ease;
            }
            .btn-close {
              background: #dc3545;
              color: white;
            }
            .btn-close:hover {
              background: #c82333;
              transform: translateY(-1px);
              box-shadow: 0 4px 8px rgba(220, 53, 69, 0.3);
            }
            .btn-download {
              background: #28a745;
              color: white;
            }
            .btn-download:hover {
              background: #218838;
              transform: translateY(-1px);
              box-shadow: 0 4px 8px rgba(40, 167, 69, 0.3);
            }
            .file-info {
              text-align: center;
              color: #6c757d;
              font-size: 12px;
              margin-top: 10px;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h2>📸 ${title || 'File Preview'}</h2>
          </div>
          <div class="content-container">
            <img src="${fileUrl}" alt="${title || 'Preview'}" onerror="this.style.display='none'; document.getElementById('error-msg').style.display='block';" />
            <div id="error-msg" style="display: none; text-align: center; padding: 40px; color: #dc3545;">
              <i style="font-size: 48px;">⚠️</i>
              <p style="margin-top: 10px;">ไม่สามารถโหลดไฟล์ได้</p>
            </div>
          </div>
          <div class="button-group">
            <button class="btn btn-download" onclick="downloadFile()">💾 ดาวน์โหลดไฟล์</button>
            <button class="btn btn-close" onclick="window.close()">✖️ ปิดหน้าต่าง</button>
          </div>
          <div class="file-info">
            <p>คลิกขวาที่รูปภาพเพื่อบันทึกหรือคัดลอก</p>
          </div>
          <script>
            function downloadFile() {
              const link = document.createElement('a');
              link.href = "${fileUrl}";
              link.download = "${title || 'file'}";
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }
          </script>
        </body>
        </html>
      `);
      previewWindow.document.close();
    } else {
      // Fallback: open file in new tab if popup is blocked
      window.open(fileUrl, '_blank');
    }
  }

  /**
   * วันตัดรอบสำหรับการอนุมัติคอร์สในหน้านี้
   * อนุมัติได้เฉพาะคอร์สที่มีวันที่เรียน "ก่อน" เดือนกรกฎาคม พ.ศ. 2569 (ค.ศ. 2026-07-01)
   */
  private readonly courseApproveCutoff = new Date(2026, 6, 1); // 1 กรกฎาคม 2026 (เดือนเริ่มที่ 0)

  /**
   * ตรวจสอบว่าคอร์สนี้อนุมัติในหน้านี้ได้หรือไม่
   * เงื่อนไข: วันที่เรียนต้องอยู่ก่อนเดือนกรกฎาคม 2569 เท่านั้น
   */
  canApproveCourse(cpd: CpdHoursResponse | undefined | null): boolean {
    if (!cpd || !cpd.studyDate) {
      return false;
    }
    const studyDate = new Date(cpd.studyDate);
    if (isNaN(studyDate.getTime())) {
      return false;
    }
    return studyDate.getTime() < this.courseApproveCutoff.getTime();
  }

  /**
   * Approve course - set isCompleted and isVerified to true, and update course status
   * หมายเหตุ: อนุมัติได้เฉพาะคอร์สเก่า (ก่อน กรกฎาคม 2568) และจะไม่เปลี่ยน paymentStatus
   */
  approveCourse(cpd: CpdHoursResponse): void {
    if (!cpd || !cpd.id) {
      console.warn('⚠️ No CPD hour record ID provided');
      return;
    }

    // กันเผื่อกรณีถูกเรียกทั้งที่ปุ่มถูก disable — คอร์สใหม่ห้ามอนุมัติในหน้านี้
    if (!this.canApproveCourse(cpd)) {
      alert('ไม่สามารถอนุมัติคอร์สนี้ในหน้านี้ได้ เนื่องจากวันที่เรียนอยู่ตั้งแต่เดือนกรกฎาคม 2569 เป็นต้นไป');
      return;
    }

    const confirmed = confirm('คุณต้องการอนุมัติคอร์สเรียนนี้หรือไม่?');
    if (!confirmed) return;

    console.log('🔄 Approving course:', cpd.id);

    const apiUrl = `${environment.apiUrl}/cpd-hours/${cpd.id}`;
    // ไม่เปลี่ยน cpdPaymentStatusId เพราะเป็นคอร์สเก่า ต้องค้างสถานะการชำระเงินไว้
    // (มิฉะนั้นระบบใบเสร็จจะรันเลขผิดพลาด)
    const payload = {
      isCompleted: true,
      isVerified: true,
      cpdCourseStatusId: 3
    };

    const sub = this.http.patch(apiUrl, payload).subscribe({
      next: (response) => {
        console.log('✅ Course approved successfully:', response);
        alert('อนุมัติคอร์สเรียนสำเร็จ');

        // Reload CPD hours to reflect the changes
        this.loadCpdHours();
      },
      error: (error) => {
        console.error('❌ Error approving course:', error);
        alert('เกิดข้อผิดพลาดในการอนุมัติคอร์สเรียน');
      }
    });

    this.subscription.add(sub);
  }
} 